import { leadsEndpoints } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export interface DuplicateLead {
  id: number;
  name: string;
  phone: string | null;
  /** `+<calling code>` split off by the server; older payloads may omit it. */
  phoneCountryCode?: string | null;
  phoneNormalized: string | null;
  email: string | null;
  createdAt: string;
  duplicateCount: number | null;
  branch?: { id: number; name: string } | null;
  leadSource?: { id: number; name: string; type?: string } | null;
  campaign?: { id: number; name: string } | null;
  LeadUsers?: { user?: { id: number; name: string } | null }[];
}

interface DuplicateListResponse {
  status: string;
  data: DuplicateLead[];
  total: number;
  meta: { page: number; limit: number; totalPages: number };
}

interface UseDuplicateLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const DUPLICATE_LEADS_KEY = ["duplicate-leads"] as const;

export function useDuplicateLeads(params: UseDuplicateLeadsParams = {}) {
  const { page = 1, limit = 20, search = "" } = params;

  return useQuery({
    queryKey: [...DUPLICATE_LEADS_KEY, { page, limit, search }],
    queryFn: async () => {
      const { data } = await axiosInstance.get<DuplicateListResponse>(
        `${leadsEndpoints}/duplicates`,
        {
          params: {
            page,
            limit,
            search: search || undefined,
          },
        }
      );
      return data;
    },
  });
}

export function invalidateDuplicateLeads(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: DUPLICATE_LEADS_KEY });
  // Deleting a duplicate also drops it from the main leads grid + counts.
  queryClient.invalidateQueries({ queryKey: ["leads"] });
  queryClient.invalidateQueries({ queryKey: ["get-leads"] });
  queryClient.invalidateQueries({ queryKey: ["leads-list-count"] });
  queryClient.invalidateQueries({ queryKey: ["leads-list-stats"] });
}
